import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { EuiButton, EuiDatePicker, EuiFieldNumber, EuiFlexGroup, EuiFlexItem, EuiFlyoutBody, EuiFlyoutFooter, EuiFlyoutHeader, EuiFormRow, EuiRadioGroup, EuiTitle } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { IntervalUnit } from 'api/interfaces'
import { MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'
import { useGetAIPlanStubQuery, useEditAIPlanStubCampaignBuilderMutation } from 'api/rtkQueryApi/platform/aiPlansApi'
import { AcEuiFormRow } from 'components/Forms/AcEuiFormRow'
import { getCampaignTypeNameLong } from 'utils/CampaignType'

export interface EditAIPlanCampaignBasicsFlyoutProps {
  campaignBuilderId: string
  onClose: () => void
}

const EditAIPlanCampaignBasicsFlyout: React.FC<EditAIPlanCampaignBasicsFlyoutProps> = ({ campaignBuilderId, onClose }) => {
  const { aiPlanId } = useParams<{ aiPlanId: string }>()
  const { data: stub } = useGetAIPlanStubQuery({ aiPlanId }, { skip: !aiPlanId })
  const didFireUpdate = useRef(false)

  const builder = stub?.campaignBuilders.find(b => b.id === campaignBuilderId)

  const [campaignType, setCampaignType] = useState(builder?.campaignType as unknown as MasterCampaignPlanCampaignBuilderType || MasterCampaignPlanCampaignBuilderType.TV)
  const [startDate, setStartDate] = useState(moment(builder?.startDate))
  const [intervalDuration, setIntervalDuration] = useState(builder?.intervalDuration || 1)
  const [intervalBudget, setIntervalBudget] = useState(builder?.intervalBudget || 0)
  const [budgetTouched, setBudgetTouched] = useState(false)
  const [cpm] = useState<number | undefined>(builder?.cpm != null ? Math.round(builder.cpm * 100) / 100 : undefined)
  const [cpc] = useState<number | undefined>(builder?.cpc != null ? Math.round(builder.cpc * 100) / 100 : undefined)
  const [marginOfError] = useState<number | undefined>(builder?.marginOfError ?? undefined)

  const [editCampaignBuilder, { isSuccess, isLoading }] = useEditAIPlanStubCampaignBuilderMutation()

  useEffect(() => {
    if (didFireUpdate.current && isSuccess) {
      didFireUpdate.current = false
      onClose()
    }
  }, [didFireUpdate, isSuccess, onClose])

  if (!builder || !stub) return null

  const intervalUnit = builder.intervalUnit || IntervalUnit.Month
  const intervalFrequency = builder.intervalFrequency || 1

  const onSave = () => {
    const request = {
      name: builder.name,
      shortSummary: builder.shortSummary,
      longSummary: builder.longSummary,
      audienceSummary: builder.audienceSummary,
      audienceDetails: builder.audienceDetails || '',
      geoSummary: builder.geoSummary,
      geoDetails: builder.geoDetails || '',
      seasonalitySummary: builder.seasonalitySummary,
      seasonalityDetails: builder.seasonalityDetails || '',
      campaignType: campaignType as unknown as string,
      isOffAdCritter: builder.isOffAdCritter,
      offAdCritterName: builder.offAdCritterName,
      offAdCritterColor: builder.offAdCritterColor,
      intervalBudget,
      retargetSite: builder.retargetSite,
      isTotalBudget: builder.isTotalBudget,
      startDate: moment(startDate).toISOString(),
      endDate: builder.endDate ? moment(builder.endDate).toISOString() : undefined,
      intervalUnit: intervalUnit as unknown as string,
      intervalFrequency,
      intervalDuration,
      segmentTargets: builder.segmentTargets || [],
      audienceSegmentLooksUp: builder.audienceSegmentLooksUp || [],
      targetingType: builder.targetingType as unknown as string | undefined,
      regions: builder.regions || [],
      dmas: builder.dmas || [],
      cities: builder.cities || [],
      zips: builder.zips || [],
      keywords: builder.keywords || [],
      keywordDescription: builder.keywordDescription || '',
      segmentDescriptions: builder.segmentDescriptions || [],
      includeInProjections: builder.includeInProjections || false,
      includeInGeoHeatmap: builder.includeInGeoHeatmap || false,
      cpm,
      cpc,
      marginOfError
    }

    editCampaignBuilder({
      aiPlanStubId: stub.id,
      campaignBuilderId,
      aiPlanId,
      request
    })
    didFireUpdate.current = true
  }

  const isSaveDisabled =
    moment(startDate).startOf('day').diff(moment().startOf('day'), 'days') < 3 ||
    intervalDuration <= 0 ||
    (!builder.isOffAdCritter ? (!intervalBudget || intervalBudget < 100) : !intervalBudget)

  return (
    <AcFlyout onClose={onClose} size='s'>
      <EuiFlyoutHeader>
        <EuiTitle size='m'>
          <h2>Edit Campaign Basics</h2>
        </EuiTitle>
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        {!builder.isOffAdCritter && (
          <EuiFormRow label='Campaign Type'>
            <EuiRadioGroup
              options={[
                { id: MasterCampaignPlanCampaignBuilderType.TV, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.TV) },
                { id: MasterCampaignPlanCampaignBuilderType.Billboard, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.Billboard) },
                { id: MasterCampaignPlanCampaignBuilderType.Audio, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.Audio) },
                { id: MasterCampaignPlanCampaignBuilderType.Internet, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.Internet) }
              ]}
              idSelected={campaignType}
              onChange={id => setCampaignType(id as MasterCampaignPlanCampaignBuilderType)}
              name='campaign type group'
            />
          </EuiFormRow>
        )}

        <EuiFormRow fullWidth label='Start Date *' isInvalid={moment(startDate).startOf('day').diff(moment().startOf('day'), 'days') < 3} error={['Start date must be at least 3 days from today']}>
          <EuiDatePicker minDate={moment().add(3, 'days')} selected={moment(startDate)} onChange={date => setStartDate(date!)} />
        </EuiFormRow>

        <EuiFormRow fullWidth label='Duration *' isInvalid={intervalDuration <= 0} error={['Duration must be greater than 0']}>
          <EuiFieldNumber value={intervalDuration} append={`${intervalFrequency > 1 ? intervalFrequency : ''} ${intervalUnit}${intervalDuration > 1 ? 's' : ''}`} onChange={e => setIntervalDuration(parseInt(e.target.value))} />
        </EuiFormRow>

        <AcEuiFormRow fullWidth label='Budget *' isInvalid={budgetTouched && (!builder.isOffAdCritter ? (!intervalBudget || intervalBudget < 100) : !intervalBudget)} error={!builder.isOffAdCritter ? ['Budget must be at least $100'] : []}>
          <EuiFieldNumber
            prepend='$'
            value={intervalBudget}
            min={!builder.isOffAdCritter ? 100 : undefined}
            onClick={() => setBudgetTouched(true)}
            onChange={e => setIntervalBudget(Number(e.target.value))}
            append={`per ${intervalUnit}${intervalFrequency > 1 ? 's' : ''}`}
          />
        </AcEuiFormRow>

      </EuiFlyoutBody>
      <EuiFlyoutFooter>
        <EuiFlexGroup direction='row' alignItems='center'>
          <EuiFlexItem grow={false}>
            <EuiButton onClick={onClose}>Cancel</EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton fill onClick={onSave} isLoading={isLoading} isDisabled={isSaveDisabled}>
              Save
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </AcFlyout>
  )
}

export default EditAIPlanCampaignBasicsFlyout
